import { Box, Typography } from '@mui/joy';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { DEFAULT_PLACEHOLDER } from 'core/constants';
import { Price, Tooltip } from 'core/elements';
import { PricingHelper } from 'pricing/helpers';
import { QuotePartHelper } from 'quotes/helpers';
import { IQuotePart } from 'quotes/types';
import { Color, Level } from 'theme/enums';

interface IProps {
  quotePart: IQuotePart;
  treatAsCost?: boolean;
  showPriceGuidance?: boolean;
}

const PriceWithWarning = ({ quotePart, treatAsCost = false, showPriceGuidance = true }: IProps) => {
  const { t } = useTranslation();

  const { includeFreight, price, priceWithFreight, quotePriceDecimals } = quotePart;
  const { corporatePricing } = quotePart?.corporatePartConfig || {};

  const floorPrice = corporatePricing?.floorPrice;
  const isPriceValid = !(price && floorPrice && price < floorPrice);

  const priceValue = includeFreight ? priceWithFreight : price;

  const isAlternateVersionEnabled = Boolean(quotePart.alternateVersionEnabled);

  const priceContent = useMemo(() => {
    const price = Number(priceValue);

    if (price) {
      // Always the line's PRICE decimals -- this renders `quote_part.price`, which the BE rounds
      // by price decimals even on a vendor quote (QuotationPartService::processPricingData keys
      // the scale off the column name). `treatAsCost` only controls zero-padding.
      const decimals = quotePriceDecimals;
      const label = showPriceGuidance
        ? QuotePartHelper.pricingLabel({
            source: QuotePartHelper.convertPriceSourceToPricingGuidanceSourceType(
              quotePart.priceSourceType
            ),
            isAlternateVersionEnabled,
          })
        : null;

      return (
        <Price
          label={label}
          amount={priceValue}
          decimals={decimals}
          fixedDecimals={treatAsCost}
          color={!isPriceValid ? Color.DANGER : null}
          labelStyle={{ textAlign: 'right', textWrap: 'auto' }}
          style={{ fontWeight: 600 }}
        />
      );
    }

    if (isAlternateVersionEnabled) {
      return (
        <Typography level={Level.BODY_XS} sx={{ textAlign: 'right', fontStyle: 'italic' }}>
          {t('labelNoPrice')}
        </Typography>
      );
    }

    return <Box textAlign="right">{DEFAULT_PLACEHOLDER}</Box>;
  }, [
    showPriceGuidance,
    isAlternateVersionEnabled,
    isPriceValid,
    priceValue,
    quotePart.priceSourceType,
    quotePriceDecimals,
    t,
    treatAsCost,
  ]);

  if (isPriceValid) {
    return priceContent;
  }

  return (
    <Tooltip
      color={Color.DANGER}
      title={t('warningPriceBelowFloorPrice', {
        floorPrice: PricingHelper.formatPricing({
          amount: floorPrice,
          decimals: quotePriceDecimals,
          fixedDecimals: treatAsCost,
        }),
      })}
      sx={{ justifyContent: 'flex-end' }}
    >
      {priceContent}
    </Tooltip>
  );
};

export default PriceWithWarning;
